import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import PageMaker from '../pages/PageMaker';
import HomePage from '../pages/Home';
// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralApp /> },
        { path: 'ecommerce', element: <GeneralEcommerce /> },
        { path: 'analytics', element: <GeneralAnalytics /> },
        { path: 'banking', element: <GeneralBanking /> },
        { path: 'booking', element: <GeneralBooking /> },

        {
          path: 'e-commerce',
          children: [
            { element: <Navigate to="/dashboard/e-commerce/shop" replace />, index: true },
            { path: 'shop', element: <EcommerceShop /> },
            { path: 'product/:name', element: <EcommerceProductDetails /> },
            { path: 'list', element: <EcommerceProductList /> },
            { path: 'product/new', element: <EcommerceProductCreate /> },
            { path: 'product/:name/edit', element: <EcommerceProductCreate /> },
            { path: 'checkout', element: <EcommerceCheckout /> },
            { path: 'invoice', element: <EcommerceInvoice /> },
          ],
        },
        {
          path: 'depo',
          children: [
            { element: <Navigate to="/dashboard/depo/list" replace />, index: true },
            // { path: 'shop', element: <EcommerceShop /> },
            { path: 'product/:id', element: <DepoProductDetails /> },
            { path: 'list', element: <DepoProductList /> },
            { path: 'product/new', element: <DepoProductCreate /> },
            { path: 'product/edit/:id', element: <DepoProductEdit /> },
            // { path: 'checkout', element: <EcommerceCheckout /> },
            // { path: 'invoice', element: <EcommerceInvoice /> },
          ],
        },
        {
          path: 'invoice',
          children: [
            { element: <Navigate to="/dashboard/invoice/invoice" replace />, index: true },
            // { path: 'shop', element: <EcommerceShop /> },
            // { path: 'product/:id', element: <DepoProductDetails /> },
            // { path: 'list', element: <DepoProductList /> },
            // { path: 'product/new', element: <DepoProductCreate /> },
            // { path: 'product/edit/:id', element: <DepoProductEdit /> },
            { path: 'create', element: <CreateInvoice /> },
            { path: 'invoice', element: <Invoice /> },
          ],
        },
        {
          path: 'panel',
          children: [
            { element: <Navigate to="/dashboard/panel/create" replace />, index: true },
            { path: 'create', element: <BlockCreate /> },
          ],
        },
        {
          path: 'accounting',
          children: [
            { element: <Navigate to="/dashboard/accounting/list" replace />, index: true },
            // { path: 'shop', element: <EcommerceShop /> },
            { path: 'transaction/single/:id', element: <TransactionSingle /> },
            { path: 'transaction/list', element: <TransactionList /> },
            { path: 'transaction/new', element: <TransactionCreate /> },
            { path: 'transaction/edit/:id', element: <TransactionEdit /> },
            // { path: 'checkout', element: <EcommerceCheckout /> },
            // { path: 'invoice', element: <EcommerceInvoice /> },
          ],
        },
        {
          path: 'tools',
          children: [
            { element: <Navigate to="/dashboard/tools/workflow" replace />, index: true },
            // { path: 'shop', element: <EcommerceShop /> },
            { path: 'workflow', element: <WorkFlow /> },
            { path: 'filemanager', element: <FileManager /> },
            { path: 'category', element: <Category /> },
            // { path: 'transaction/new', element: <TransactionCreate /> },
            // { path: 'transaction/edit/:id', element: <TransactionEdit /> },
            // { path: 'checkout', element: <EcommerceCheckout /> },
            // { path: 'invoice', element: <EcommerceInvoice /> },
          ],
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            { path: 'profile', element: <UserProfile /> },
            { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
          ],
        },
        {
          path: 'customer',
          children: [
            { element: <Navigate to="/dashboard/customer/profile" replace />, index: true },
            { path: 'profile/:id', element: <CustomerProfile /> },
            // { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <CustomerList /> },
            { path: 'new', element: <CustomerCreate /> },
            { path: 'edit/:id', element: <CustomerEdit /> },
            { path: 'account', element: <CustomerAccount /> },
          ],
        },
        {
          path: 'ticket',
          children: [
            { element: <Navigate to="/dashboard/ticket/list" replace />, index: true },
            // { path: 'profile/:id', element: <CustomerProfile /> },
            // { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <TicketList /> },
            // { path: 'new', element: <CustomerCreate /> },
            // { path: 'edit/:id', element: <CustomerEdit /> },
            // { path: 'account', element: <CustomerAccount /> },
          ],
        },
        {
          path: 'blog',
          children: [
            { element: <Navigate to="/dashboard/blog/posts" replace />, index: true },
            { path: 'posts', element: <BlogPosts /> },
            { path: 'post/:title', element: <BlogPost /> },
            { path: 'new-post', element: <BlogNewPost /> },
          ],
        },
        {
          path: 'mail',
          children: [
            { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
            { path: 'label/:customLabel', element: <Mail /> },
            { path: 'label/:customLabel/:mailId', element: <Mail /> },
            { path: ':systemLabel', element: <Mail /> },
            { path: ':systemLabel/:mailId', element: <Mail /> },
          ],
        },
        {
          path: 'chat',
          children: [
            { element: <Chat />, index: true },
            { path: 'new', element: <Chat /> },
            { path: ':conversationKey', element: <Chat /> },
          ],
        },
        { path: 'calendar', element: <Calendar /> },
        { path: 'kanban', element: <Kanban /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { path: '/',element: <HomePage /> },
        { path: '/:page',element: <PageMaker /> },
        // { path: 'about-us/', element: <About /> },
        // { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const DepoProductDetails = Loadable(lazy(() => import('../pages/dashboard/DepoProductDetails')));
const DepoProductList = Loadable(lazy(() => import('../pages/dashboard/DepoProductList')));
const DepoProductCreate = Loadable(lazy(() => import('../pages/dashboard/DepoProductCreate')));
const CreateInvoice = Loadable(lazy(() => import('../pages/dashboard/CreateInvoice')));
const Invoice = Loadable(lazy(() => import('../pages/dashboard/Invoice')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const DepoProductEdit = Loadable(lazy(() => import('../pages/dashboard/DepoProductEdit')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')));
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
const CustomerList = Loadable(lazy(() => import('../pages/dashboard/CustomerList')));
const CustomerCreate = Loadable(lazy(() => import('../pages/dashboard/CustomerCreate')));
const CustomerEdit = Loadable(lazy(() => import('../pages/dashboard/CustomerEdit')));
const CustomerAccount = Loadable(lazy(() => import('../pages/dashboard/CustomerAccount')));
const CustomerProfile = Loadable(lazy(() => import('../pages/dashboard/CustomerProfile')));
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));
// Panel Website
const BlockCreate = Loadable(lazy(() => import('../pages/dashboard/BlockCreate')));
// Accounting 
const TransactionCreate = Loadable(lazy(() => import('../pages/dashboard/TransactionCreate')));
const TransactionList = Loadable(lazy(() => import('../pages/dashboard/TransactionList')));
const TransactionSingle = Loadable(lazy(() => import('../pages/dashboard/TransactionSingle')));
const TransactionEdit = Loadable(lazy(() => import('../pages/dashboard/TransactionEdit')));
// Tools
const WorkFlow = Loadable(lazy(() => import('../pages/dashboard/WorkFlow')));
const FileManager = Loadable(lazy(() => import('../pages/dashboard/FileManager')));
const Category = Loadable(lazy(() => import('../pages/dashboard/Category')));
// Ticket
const TicketList = Loadable(lazy(() => import('../pages/dashboard/TicketList')));
// Main
const About = Loadable(lazy(() => import('../pages/PageMaker')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
